import { useCallback, useEffect, useEffectEvent, useState } from "react";
import { useOpenState } from "../../../hooks/useOpenState";
import { useLoadingModal } from "../../../components/primitives/feedback/LoadingModal";
import { useAnnotationFieldTimer } from "../../../hooks/useAnnotationFieldTimer";
import { useSearchParams } from "react-router-dom";
import { SCORECARD_VOCAB } from "@shared/scorecard";
import {
  getNextBacklogItem,
  getDocument,
  getDocumentMarkdown,
  getMonoAiLabels,
  getPrevalidatedField,
  rejectDocument,
  saveAnnotation,
  type AnnotationDimension,
  type DimensionReviewState,
  type ExtractedScorecardWithEvidence,
  type OverrideInteraction,
  type PrevalidatedField,
  type QueueRow,
  type ScorecardKey,
  type ScorecardKind,
} from "../../../lib/corpusApi";

/** Outcome banner shown after a save, before the next item loads. */
export interface SaveOutcome {
  status: "ingested" | "pending";
  reason: string;
}

export interface UseAnnotationReviewResult {
  /** The field this stream is scoped to, or undefined for the system's free pick. */
  scopedField: ScorecardKey | undefined;
  field: ScorecardKey | null;
  document: QueueRow | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  /** True once the backlog (or this field's slice of it) is empty. */
  done: boolean;
  outcome: SaveOutcome | null;
  /** "multi" when the current document has a multi-candidate scorecard; "mono" otherwise. */
  scorecardKind: ScorecardKind;
  /** Mono-voter path only: the extracted scorecard keyed by ScorecardKey. */
  monoScorecard: ExtractedScorecardWithEvidence | null;
  /** Grouped override-mode state and callbacks — pass directly as panel `override` prop. */
  override: OverrideInteraction;
  /** Open override mode — pass as panel `onOverride` prop. */
  openOverride: () => void;
  /** Mono path: approve the single AI suggestion. Noop when scorecardKind is "multi". */
  approve: () => void;
  /** Multi path: accept candidate N's labels (0-indexed). Noop when scorecardKind is "mono". */
  acceptCandidate: (candidateIndex: number) => void;
  /** The pipeline's pre-validated settlement for the active field, or null when it wasn't
   *  fast-pathed. Present only on multi documents. */
  prevalidatedField: PrevalidatedField | null;
  /** Multi path: approve the pre-validated pipeline value for the active field. Noop when the
   *  field has no pre-validated settlement. */
  acceptPrevalidated: () => void;
  markEmpty: () => void;
  /** Reject the current document and advance to the next item. Throws on failure. */
  rejectAndAdvance: () => Promise<void>;
  /** Whether the inline help panel is expanded. */
  helpOpen: boolean;
  /** Toggle the inline help panel. */
  onHelpToggle: () => void;
}

/** Builds the AnnotationDimension state for a save, matching mergeHumanAnnotation's contract on
 *  the backend: any AI suggestion overridden (even to nothing) is "ai_overridden"; a field the
 *  AI found nothing for is "empty" or "human_filled" depending on the human's selection. */
function reviewState(aiLabels: string[], selection: string[]): DimensionReviewState {
  if (aiLabels.length === 0) return selection.length === 0 ? "empty" : "human_filled";
  return "ai_overridden";
}

/** Next unreviewed field in SCORECARD_VOCAB order, skipping fields already in doc.annotation. */
function pickNextUnreviewedField(doc: QueueRow): ScorecardKey | null {
  const keys = Object.keys(SCORECARD_VOCAB) as ScorecardKey[];
  for (const key of keys) {
    if (!doc.annotation?.[key]) return key;
  }
  return null;
}

export function useAnnotationReview(): UseAnnotationReviewResult {
  const [searchParams] = useSearchParams();
  const scopedField = (searchParams.get("field") as ScorecardKey | null) ?? undefined;
  // When present, bypass the backlog and annotate this specific document field-by-field.
  const sourceId = searchParams.get("sourceId") ?? undefined;

  const { withLoadingModal, loading: saving } = useLoadingModal();

  const [field, setField] = useState<ScorecardKey | null>(null);
  const [document, setDocument] = useState<QueueRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [outcome, setOutcome] = useState<SaveOutcome | null>(null);

  const [overrideMode, setOverrideModeState] = useState(false);
  const [overrideFocusIndex, setOverrideFocusIndex] = useState(0);
  const [overrideSelection, setOverrideSelection] = useState<string[]>([]);
  const help = useOpenState();

  const loadNext = useCallback(async () => {
    setLoading(true);
    setError(null);
    setOutcome(null);
    setOverrideModeState(false);
    try {
      if (sourceId) {
        // Direct document mode: load by ID, iterate through fields in SCORECARD_VOCAB order.
        const [doc, markdown_text] = await Promise.all([
          getDocument(sourceId),
          getDocumentMarkdown(sourceId),
        ]);
        const fullDoc = { ...doc, markdown_text };
        const nextField = pickNextUnreviewedField(fullDoc);
        if (!nextField) {
          setDone(true);
          setDocument(null);
          setField(null);
          return;
        }
        setDone(false);
        setField(nextField);
        setDocument(fullDoc);
      } else {
        // Normal backlog mode.
        let item = await getNextBacklogItem(scopedField);
        if (!item && scopedField) {
          // This field's slice of the backlog is clear — fall through to the system's free pick
          // rather than reporting the whole backlog as done.
          item = await getNextBacklogItem(undefined);
        }
        if (!item) {
          setDone(true);
          setDocument(null);
          setField(null);
          return;
        }
        setDone(false);
        setField(item.field);
        const [doc, markdown_text] = await Promise.all([
          getDocument(item.sourceId),
          getDocumentMarkdown(item.sourceId),
        ]);
        setDocument({ ...doc, markdown_text });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load the next item");
    } finally {
      setLoading(false);
    }
  }, [sourceId, scopedField]);

  useEffect(() => {
    void loadNext();
  }, [loadNext]);

  const timer = useAnnotationFieldTimer({
    docUrl: document?.url ?? null,
    field,
    loading,
  });

  const commit = useCallback(
    async (labels: string[], state: DimensionReviewState) => {
      const doc = document;
      const key = field;
      if (!doc || !key) return;

      timer.record(labels, state);

      setError(null);
      try {
        await withLoadingModal(async () => {
          const entry: AnnotationDimension = { labels, state };
          const merged = { ...(doc.annotation ?? {}), [key]: entry };
          const response = await saveAnnotation(doc.id, merged, doc.metadata ?? {});
          setOutcome({ status: response.status, reason: response.reason });
          await loadNext();
        }, "Saving annotation…");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save");
      }
    },
    [document, field, loadNext, timer, withLoadingModal]
  );

  function setOverrideMode(v: boolean) {
    setOverrideModeState(v);
    if (v) {
      const key = field;
      const doc = document;
      const current = key ? doc?.annotation?.[key] : undefined;
      // Seed override from the settled value: a prevalidated field's pipeline labels, else the
      // mono AI suggestion. Multi candidate fields have no single suggestion, so they start empty.
      const aiLabels = prevalidatedField
        ? prevalidatedField.labels
        : getMonoAiLabels(doc?.ai_scorecard ?? null, key);
      setOverrideSelection(current?.labels ?? aiLabels);
      setOverrideFocusIndex(0);
    }
  }

  function confirmOverride() {
    const key = field;
    const doc = document;
    const aiLabels = prevalidatedField
      ? prevalidatedField.labels
      : getMonoAiLabels(doc?.ai_scorecard ?? null, key);
    const selection = overrideSelection;
    setOverrideModeState(false);
    void commit(selection, reviewState(aiLabels, selection));
  }

  function cancelOverride() {
    setOverrideModeState(false);
    setOverrideSelection([]);
  }

  // Derived scorecard shape — recomputed from document whenever it changes.
  const scorecardKind = document?.ai_scorecard?.kind ?? "mono";
  const monoScorecard =
    document?.ai_scorecard?.kind === "mono" ? document.ai_scorecard.scorecard : null;
  // The pipeline's pre-validated settlement for the active field (multi docs only). When present,
  // the field wasn't drafted by the panel — it's shown pre-approved for a one-key confirm.
  const prevalidatedField = getPrevalidatedField(document?.ai_scorecard ?? null, field);

  function acceptPrevalidated() {
    if (scorecardKind !== "multi" || !prevalidatedField) return;
    void commit(prevalidatedField.labels, "ai_approved");
  }

  function approve() {
    if (scorecardKind !== "mono") return;
    const key = field;
    const aiLabels = key ? (monoScorecard?.[key]?.labels ?? []) : [];
    if (aiLabels.length === 0) return;
    void commit(aiLabels, "ai_approved");
  }

  function acceptCandidate(candidateIndex: number) {
    if (scorecardKind !== "multi") return;
    const key = field;
    const doc = document;
    if (!key || !doc || doc.ai_scorecard?.kind !== "multi") return;
    const candidateDrafts = Object.values(doc.ai_scorecard.candidates);
    const draft = candidateDrafts[candidateIndex]?.[key];
    if (!draft) return;
    void commit(draft.labels, "ai_approved");
  }

  function markEmpty() {
    const key = field;
    const aiLabels = scorecardKind === "mono" && key ? (monoScorecard?.[key]?.labels ?? []) : [];
    void commit([], reviewState(aiLabels, []));
  }

  const rejectAndAdvance = useCallback(async () => {
    const doc = document;
    if (!doc) return;
    timer.record([], "document_rejected");
    await rejectDocument(doc.id);
    await loadNext();
  }, [document, loadNext, timer]);

  // Keyboard shortcuts.
  // Override mode (both kinds): Enter confirms, Esc cancels (↑/↓/Space belong to ChoiceGroup).
  // Mono mode: Enter approves; O opens override; E marks empty.
  // Multi mode: 1/2/3/… accept candidate N (1-indexed); O opens override; E marks empty.
  //             Enter is intentionally a noop — a choice must be made explicitly.
  // An Effect Event: this is a persistent window-level subscription that must always act on the
  // latest saving/loading/done/overrideMode/field/document/etc, but should not tear down and
  // re-subscribe every time one of those changes (e.g. on every override-selection keystroke).
  // See platform/CLAUDE.md "React hooks — hard rules".
  const handleKeyDown = useEffectEvent((e: KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;
    if (saving || loading || done) return;

    if (overrideMode) {
      if (e.key === "Enter") {
        e.preventDefault();
        confirmOverride();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelOverride();
      }
      return;
    }

    if (scorecardKind === "multi") {
      // Pre-validated field: Enter confirms the settled pipeline value (like mono approve).
      if (prevalidatedField && e.key === "Enter") {
        e.preventDefault();
        acceptPrevalidated();
        return;
      }
      const candidateDigit = parseInt(e.key, 10);
      if (!isNaN(candidateDigit) && candidateDigit >= 1) {
        e.preventDefault();
        acceptCandidate(candidateDigit - 1);
        return;
      }
    } else {
      // Mono: Enter approves.
      if (e.key === "Enter") {
        e.preventDefault();
        approve();
        return;
      }
    }

    if (e.key === "o" || e.key === "O") {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      setOverrideMode(true);
    } else if (e.key === "e" || e.key === "E") {
      if (e.metaKey || e.ctrlKey) return;
      e.preventDefault();
      markEmpty();
    } else if (e.key === "?") {
      e.preventDefault();
      help.toggle();
    }
  });

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      handleKeyDown(e);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return {
    scopedField,
    field,
    document,
    loading,
    error,
    saving,
    done,
    outcome,
    scorecardKind,
    monoScorecard,
    override: {
      mode: overrideMode,
      focusIndex: overrideFocusIndex,
      selection: overrideSelection,
      onFocusChange: setOverrideFocusIndex,
      onSelectionChange: setOverrideSelection,
      onConfirm: confirmOverride,
      onCancel: cancelOverride,
    },
    openOverride() {
      setOverrideMode(true);
    },
    approve,
    acceptCandidate,
    prevalidatedField,
    acceptPrevalidated,
    markEmpty,
    rejectAndAdvance,
    helpOpen: help.open,
    onHelpToggle: help.toggle,
  };
}
